import { useMutation, UseMutationResult } from '@tanstack/react-query'

import { brand } from '../utils/brand'
import { getV6ApiUrl } from '../utils/urlHelpers'

import {
  getRudderstackAnonymousId,
  getRudderstackAnonymousIdWithTimeout,
} from './useRudderstackAnalytics'

export interface CreateWelcomeLeadPayload {
  email: string
  emailSubscribed?: boolean
  anonymousId?: string
}

export interface CreateWelcomeLeadResponse {
  success: boolean
  leadId?: string
  errors?: string[]
}

// Page-lifetime dedupe set shared across all callers (the Braze iframe
// submit listener in useBraze.ts, the postMessage handler in ClientPortal,
// the React Query mutation below). Prevents duplicate POSTs when more than
// one path fires for the same lead submission.
const submittedLeads = new Set<string>()

// Test-only helper: clears the dedupe set so each test starts fresh.
// Production code should not call this — it would defeat the dedup.
export const _resetSubmittedLeadsForTesting = (): void => {
  submittedLeads.clear()
}

export const postCreateWelcomeLead = async (
  payload: CreateWelcomeLeadPayload,
): Promise<CreateWelcomeLeadResponse> => {
  const email = payload.email.trim()

  // Dedupe before the network call. Marking before the fetch (vs after success)
  // means a network failure leaves the email blocked until the page reloads —
  // acceptable for lead capture, where the user can re-enter the form.
  if (email && submittedLeads.has(email)) {
    return { success: true, leadId: undefined }
  }
  if (email) submittedLeads.add(email)

  const url = getV6ApiUrl('/leads')

  // The backend keys the guest generate_lead on this id (Braze alias), so it
  // must be the browser SDK's. Read it synchronously first so a loaded SDK
  // keeps the POST on the submit tick; only when it is still unknown (SDK not
  // loaded yet, or the ClientPortal postMessage path that passes nothing) take
  // the bounded wait — this POST is fire-and-forget, so a longer wait is
  // affordable here.
  const anonymousId =
    payload.anonymousId ??
    getRudderstackAnonymousId() ??
    (await getRudderstackAnonymousIdWithTimeout(3000))

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-brand-id': brand.key },
    body: JSON.stringify({
      email,
      email_subscribed: payload.emailSubscribed ?? false,
      brand: brand.key,
      ...(anonymousId ? { anonymous_id: anonymousId } : {}),
    }),
  })

  const json = (await response.json()) as {
    data?: { id?: string | null; email?: string; email_subscribed?: boolean } | null
    status: 'success' | 'error'
    code: number
    errors?: string[]
  }

  return {
    success: json.status === 'success',
    leadId: json.data?.id ?? undefined,
    errors: json.errors,
  }
}

const useCreateWelcomeLead = (): UseMutationResult<
  CreateWelcomeLeadResponse,
  Error,
  CreateWelcomeLeadPayload,
  unknown
> => {
  return useMutation<CreateWelcomeLeadResponse, Error, CreateWelcomeLeadPayload>({
    mutationFn: postCreateWelcomeLead,
  })
}

export default useCreateWelcomeLead
